import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {
  useContentSourceQuery,
  useSetContentSourceMutation,
} from '#/state/queries/content-source'
import * as Toast from '#/view/com/util/Toast'
import * as SettingsList from '#/screens/Settings/components/SettingsList'
import {atoms as a, useTheme} from '#/alf'
import * as SegmentedControl from '#/components/forms/SegmentedControl'
import {Earth_Stroke2_Corner2_Rounded as EarthIcon} from '#/components/icons/Globe'
import * as Layout from '#/components/Layout'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<
  CommonNavigatorParams,
  'ContentSourceSettings'
>

export function ContentSourceSettingsScreen({}: Props) {
  const {t: l} = useLingui()
  const t = useTheme()
  const {data, isLoading} = useContentSourceQuery()
  const {mutate: setSource, isPending} = useSetContentSourceMutation()

  const available = data?.available ?? (['federated', 'wsocial'] as const)
  const current = data?.source ?? 'federated'

  const handleChange = (next: string) => {
    if (next === current || isPending) return
    setSource(next as 'federated' | 'wsocial', {
      onError: () =>
        Toast.show(
          l`Failed to switch content source. Please try again.`,
          'xmark',
        ),
    })
  }

  const labels: Record<string, string> = {
    federated: l`Federated (Bluesky)`,
    wsocial: l`W Social`,
  }

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Content source</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>
        <SettingsList.Container>
          <SettingsList.Group>
            <SettingsList.ItemIcon
              icon={isLoading || isPending ? Loader : EarthIcon}
            />
            <SettingsList.ItemText>
              <Trans>AppView</Trans>
            </SettingsList.ItemText>
            <View style={[a.flex_1, a.pt_xs]}>
              <SegmentedControl.Root
                type="radio"
                label={l`AppView selection`}
                value={current}
                onChange={handleChange}>
                {available.map(src => (
                  <SegmentedControl.Item
                    key={src}
                    label={labels[src] ?? src}
                    value={src}
                    disabled={src === current}>
                    <SegmentedControl.ItemText>
                      {labels[src] ?? src}
                    </SegmentedControl.ItemText>
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            </View>
          </SettingsList.Group>
          <SettingsList.Item iconInset>
            <Text
              style={[a.text_sm, t.atoms.text_contrast_medium, a.leading_snug]}>
              <Trans>
                Federated shows content from the whole Bluesky network. W Social
                shows only W Social content.
              </Trans>
            </Text>
          </SettingsList.Item>
        </SettingsList.Container>
      </Layout.Content>
    </Layout.Screen>
  )
}
